import plandayLogo from '../shared/images/plandayLogoWhite.svg';
import {Headers} from './HelmetHeaders';
import IconWithText from './IconWithText';
import AccountForm from './components/AccountForm';
import BusinessForm from './components/BusinessForm';
import PortalCreationProgress from './components/PortalCreationProgress';
import TeamForm from './components/TeamForm';
import {MOBILE_BREAKPOINT, TABLET_BREAKPOINT} from './constants';
import {useIntercom, useWindowDimensions} from './hooks';
import {labels} from './translations';
import {SignupForm, Step} from './types';
import {useModel} from '@planday-corp/react-model';
import Stepper from '@planday-corp/ui/indicators/Stepper';
import {Color, white} from '@planday-corp/ui/styles/colors';
import Text from '@planday-corp/ui/text/Text';
import {useState} from 'react';
import styled, {css} from 'styled-components';
import backgroundPath from '~/shared/images/signupBackground.svg';

const tablets = (style: string) => `
  @media (max-width: ${TABLET_BREAKPOINT}px) {
    ${style}
  }`;

const mobiles = (style: string) => `
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${style}
  }`;

const StyledBackground = styled.div`
  background: ${white};
  display: flex;
  min-height: 100%;
  background: url(${backgroundPath});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  ${tablets(`padding: 1rem;`)}
`;

const ColumnLayout = styled.div`
  display: flex;
  gap: 2rem;
  width: 100%;
  margin: 0 auto;
  ${tablets(
    `flex-direction: column-reverse;
    justify-content: flex-end;`
  )}
`;

const Column = styled.div<{$width?: string; $background?: Color}>`
  display: flex;
  padding: 0 1rem;
  flex-basis: ${({$width}) => $width || '50%'};
  ${({$background}) =>
    $background &&
    css`
      background: ${$background};
    `}
  justify-content: center;
  align-items: center;
  ${tablets(
    `flex-grow: 0;
    max-height: fit-content;
    margin: 0 auto;`
  )}
`;

const FormWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 2rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 33.5rem;
  padding: 2rem;
  max-height: 100vh;
  ${tablets(
    `padding: 1rem;
    gap: 1rem;
    width: fit-content;
    max-width: 33.5rem;
    max-height: none;`
  )}
  ${mobiles(`
    padding: 1rem 0;`)}
`;

const StyledLogo = styled.img`
  width: 12rem;
  height: auto;
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  ${tablets(
    `position: static;
    margin-top: 1rem;
    margin-bottom: 0;`
  )}
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DescriptionTextWrapper = styled.div`
  ${tablets(`display: none;`)}
`;

const StyledText = styled(Text)`
  margin-bottom: 1rem;
`;

const StyledIconWithText = styled(IconWithText)`
  margin-bottom: 1rem;
`;

export const EMPTY_FORM: Partial<SignupForm> = {
  email: '',
  password: '',
  companyName: '',
  countryCode: '',
  phoneNumber: undefined,
  industry: '',
  employees: '',
  firstName: '',
  lastName: '',
  termsAndConditions: undefined,
  phoneCountryCodePrefix: '',
  marketingCommunicationsAccepted: true,
};

export default function Signup() {
  const [step, setStep] = useState<Step>('account');
  const {data: form, update} = useModel(EMPTY_FORM);
  const {width: windowWidth} = useWindowDimensions();
  const STEPS_ORDER: Step[] = [
    'account',
    'business',
    'team',
    'portalCreationProgress',
  ];

  useIntercom();

  const RightColumnContent = () => {
    //ToDo: Uncomment this when the ScheduleDemo component is ready
    // if (step !== 'account' && SHOW_TEAM_STEP) {
    //   //ToDo: Add employeesNames list!!
    //   return (
    //     <ScheduleDemo
    //       companyName={form.companyName}
    //       employeesNames={[{firstName: 'John', lastName: 'Doe'}]}
    //     />
    //   );
    // }
    return (
      <DescriptionContainer>
        <DescriptionTextWrapper>
          <StyledText size="title1" color="white" element="h1">
            {labels.title}
          </StyledText>

          <StyledIconWithText
            text={labels.descriptionPoint1}
            icon="checkmarkLarge"
            color="white"
          />

          <StyledIconWithText
            text={labels.descriptionPoint2}
            icon="checkmarkLarge"
            color="white"
          />
        </DescriptionTextWrapper>
      </DescriptionContainer>
    );
  };

  return (
    <>
      <Headers />
      <StyledBackground>
        {step === 'portalCreationProgress' ? (
          <PortalCreationProgress
            // When we reach here, the form is filled out. We can cast it to the SignupForm type.
            form={form as unknown as SignupForm}
            setStep={setStep}
          />
        ) : (
          <ColumnLayout>
            <Column $width="40%" $background={white}>
              <FormWrapper>
                <Stepper
                  steps={[
                    {
                      label: labels.stepperAccountStep,
                    },
                    {
                      label: labels.stepperBusinessStep,
                    },
                    {
                      label: labels.stepperTeamStep,
                    },
                  ]}
                  activeStep={STEPS_ORDER.indexOf(step)}
                  labelsOrientation={
                    windowWidth >= MOBILE_BREAKPOINT ? 'horizontal' : 'vertical'
                  }
                />
                {step === 'account' && (
                  <AccountForm form={form} update={update} setStep={setStep} />
                )}
                {step === 'business' && (
                  <BusinessForm form={form} update={update} setStep={setStep} />
                )}
                {step === 'team' && <TeamForm setStep={setStep} />}
              </FormWrapper>
            </Column>
            <Column $width="60%">
              <StyledLogo src={plandayLogo} />
              <RightColumnContent />
            </Column>
          </ColumnLayout>
        )}
      </StyledBackground>
    </>
  );
}
